import { Observable, switchMap, withLatestFrom } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { DocViewerStore } from '@store/legacy/doc-viewer.store';
import { CustomCodingFieldsValuesManager } from '@utilities/custom-coding-fields-values-manager';
import { UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { CustomCodingFieldsManager } from '@models/review/custom-coding-fields-manager';
import { Injectable } from '@angular/core';
import { LexbeDoc } from '@models/doc.model';
import { ReviewSdk } from '../sdks/review.sdk';

@Injectable({ providedIn: 'root' })
export class DocViewerFormEngine {
	constructor(
		private docViewerStore: DocViewerStore,
		private customCodingFieldsValuesManager: CustomCodingFieldsValuesManager,
		private customCodingFieldsManager: CustomCodingFieldsManager,
		private fb: UntypedFormBuilder,
		private reviewSdk: ReviewSdk
	) {
	}

	public createDocumentFormGroup(current: LexbeDoc): void {
		this.docViewerStore.documentDataFormGroup = this.fb.group({
			batesPrefix: [''],
			batesNumberStart: [''],
			batesNumberDigits: [''],
			docSource: [''],
			custodianId: [''],
			docDate: [''],
			docTitle: [''],
			fromField: [''],
			toField: [''],
			cc: [''],
			bcc: [''],
			subjectMatter: [''],
			author: [''],
			lastModifiedBy: [''],
			docType: [''],
			description: [''],
			folderId: [''],
			sourceFilePath: [''],
			coding: this.fb.group({
				isKey: [''],
				responsive: [''],
				confidential: [''],
				customDesignation: [''],
				privilege: [''],
				workProduct: [''],
				redactForPrivilege: [''],
				redactForPi: ['']
			}),
			review: this.fb.group({
				attorneyComments: ['']
			})
		});

		this.docViewerStore.documentCodingFormGroup = this.docViewerStore.documentDataFormGroup.controls['coding'] as UntypedFormGroup;
		this.docViewerStore.documentReviewFormGroup = this.docViewerStore.documentDataFormGroup.controls['review'] as UntypedFormGroup;
		const formState = {
			batesPrefix: current?.bates?.batesPrefix,
			batesNumberStart: current?.bates?.batesNumberStart,
			batesNumberDigits: current?.bates?.batesNumberDigits,
			docSource: current?.common?.docSource,
			custodianId: current?.custodianId,
			docDate: current?.native?.docDate,
			docTitle: current?.common?.docTitle,
			fromField: current?.email?.fromField,
			toField: current.email.toField,
			cc: current.email.cc,
			bcc: current.email.bcc,
			subjectMatter: current.email.subjectMatter,
			author: current.native.author,
			lastModifiedBy: current.native.lastModifiedBy,
			docType: current.common.docType,
			description: current.coding.description,
			folderId: current.folderId,
			sourceFilePath: current?.common?.sourceFilePath,
			coding: {
				isKey: current?.coding?.isKey,
				responsive: current.coding.responsive,
				confidential: current.coding.confidential,
				customDesignation: current.coding.customDesignation,
				privilege: current.coding.privilege,
				workProduct: current.coding.workProduct,
				redactForPrivilege: current.coding.redactForPrivilege,
				redactForPi: current.coding.redactForPi
			},
			review: {
				attorneyComments: current.review.attorneyComments
			}
		};
		this.docViewerStore.documentDataFormGroup.reset(formState);
		this.docViewerStore.documentCodingFormGroup.addValidators(redactForPrivilegeValidValidator());
		this.customCodingFieldsManager.retrieve(true);
		this.customCodingFieldsValuesManager.reset();
	}

	public save(): Observable<void> {
		return this.docViewerStore.currentDocument$.pipe(
			take(1),
			withLatestFrom(this.docViewerStore.simpleState$),
			switchMap(([currentDocumentSnapshot, currentStateSnapshot]) => {
				this.customCodingFieldsValuesManager.populateDoc(currentDocumentSnapshot);
				currentStateSnapshot.reviewedByMe = true;
				currentDocumentSnapshot.coding.hasBeenReviewed = (currentStateSnapshot.reviewedByMe || currentStateSnapshot.reviewedByOthers);
				currentDocumentSnapshot.bates.batesPrefix = this.docViewerStore.documentDataFormGroup.get('batesPrefix').value;
				currentDocumentSnapshot.bates.batesNumberStart = this.docViewerStore.documentDataFormGroup.get('batesNumberStart').value;
				currentDocumentSnapshot.bates.batesNumberDigits = this.docViewerStore.documentDataFormGroup.get('batesNumberDigits').value;
				currentDocumentSnapshot.common.docSource = this.docViewerStore.documentDataFormGroup.get('docSource').value;
				currentDocumentSnapshot.custodianId = this.docViewerStore.documentDataFormGroup.get('custodianId').value;
				currentDocumentSnapshot.native.docDate = this.docViewerStore.documentDataFormGroup.get('docDate').value;
				currentDocumentSnapshot.common.docTitle = this.docViewerStore.documentDataFormGroup.get('docTitle').value;
				currentDocumentSnapshot.email.fromField = this.docViewerStore.documentDataFormGroup.get('fromField').value;
				currentDocumentSnapshot.email.toField = this.docViewerStore.documentDataFormGroup.get('toField').value;
				currentDocumentSnapshot.email.cc = this.docViewerStore.documentDataFormGroup.get('cc').value;
				currentDocumentSnapshot.email.bcc = this.docViewerStore.documentDataFormGroup.get('bcc').value;
				currentDocumentSnapshot.email.subjectMatter = this.docViewerStore.documentDataFormGroup.get('subjectMatter').value;
				currentDocumentSnapshot.native.author = this.docViewerStore.documentDataFormGroup.get('author').value;
				currentDocumentSnapshot.native.lastModifiedBy = this.docViewerStore.documentDataFormGroup.get('lastModifiedBy').value;
				currentDocumentSnapshot.common.docType = this.docViewerStore.documentDataFormGroup.get('docType').value;
				currentDocumentSnapshot.coding.description = this.docViewerStore.documentDataFormGroup.get('description').value;
				currentDocumentSnapshot.folderId = this.docViewerStore.documentDataFormGroup.get('folderId').value;
				currentDocumentSnapshot.common.sourceFilePath = this.docViewerStore.documentDataFormGroup.get('sourceFilePath').value;
				currentDocumentSnapshot.coding.isKey = this.docViewerStore.documentCodingFormGroup.get('isKey').value;
				currentDocumentSnapshot.coding.responsive = this.docViewerStore.documentCodingFormGroup.get('responsive').value;
				currentDocumentSnapshot.coding.confidential = this.docViewerStore.documentCodingFormGroup.get('confidential').value;
				currentDocumentSnapshot.coding.customDesignation = this.docViewerStore.documentCodingFormGroup.get('customDesignation').value;
				currentDocumentSnapshot.coding.privilege = this.docViewerStore.documentCodingFormGroup.get('privilege').value;
				currentDocumentSnapshot.coding.workProduct = this.docViewerStore.documentCodingFormGroup.get('workProduct').value;
				currentDocumentSnapshot.coding.redactForPrivilege = this.docViewerStore.documentCodingFormGroup.get('redactForPrivilege').value;
				currentDocumentSnapshot.coding.redactForPi = this.docViewerStore.documentCodingFormGroup.get('redactForPi').value;
				currentDocumentSnapshot.review.attorneyComments = this.docViewerStore.documentReviewFormGroup.get('attorneyComments').value;
				let reviewStatus = 0;
				if (currentStateSnapshot.reviewedByMe) reviewStatus++;
				if (currentStateSnapshot.reviewedByOthers) reviewStatus += 2;

				return this.reviewSdk.saveDocumentReviewState$(currentDocumentSnapshot, reviewStatus, currentStateSnapshot.shouldPropagateCoding, currentStateSnapshot.reviewSetId);
			}),
			tap(() => this.docViewerStore.reset())
		);
	}
}
export function redactForPrivilegeValidValidator(): ValidatorFn {
	return (control: UntypedFormGroup): ValidationErrors | null => {
		if (control.controls['redactForPrivilege'].value === true) {
			const isWithheld = control.controls['privilege'].value || control.controls['workProduct'].value;
			return !isWithheld ? { redactForPrivilegeInvalid: { value: control.value } } : null;
		}
		return null;
	};
}
